<script>
import { defineAsyncComponent, ref, computed, watch } from 'vue';

import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';

import { resetOptimisticSettings } from 'dashboard/composables/useOptimisticSetting';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import { inheritsWorkspaceV2Theme } from 'dashboard/helper/workspaceV2';
import WorkspaceV2Shell from 'dashboard/components-next/workspaceV2/WorkspaceV2Shell.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  definirContaAtual,
  garantirMigracaoDeArmazenamento,
} from 'dashboard/helper/accountScopedStorage';
import { useAccountBootstrapData } from 'dashboard/composables/useAccountBootstrapData';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useWindowSize } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

// SiteUp: popup de confirmação de agendamento WAHA — montado no shell do
// dashboard pra disparar via evento global de qualquer tela sob ele (ex.: o
// painel de Kanban da conversa). A tela do Kanban (KanbanView) tem o seu próprio.
import KanbanAppointmentModal from 'dashboard/routes/dashboard/kanban/components/KanbanAppointmentModal.vue';
import { useKanbanAppointmentPopup } from 'dashboard/composables/useKanbanAppointmentPopup';
// SiteUp: Central de Agendamento — abre por evento global de qualquer tela
// (card do Hub / item Agenda do Modo Foco).
import AgendaQuickPanel from 'dashboard/routes/dashboard/kanban/agenda/AgendaQuickPanel.vue';
import { useAgendaQuickPanel } from 'dashboard/composables/useAgendaQuickPanel';

import MobileBottomNav from 'dashboard/components-next/sidebar/MobileBottomNav.vue';
import FocusModeOverlay from 'dashboard/components-next/sidebar/FocusModeOverlay.vue';
import PageCoach from 'dashboard/components-next/onboarding/PageCoach.vue';
import PageManual from 'dashboard/components-next/onboarding/PageManual.vue';
import GuidedTour from 'dashboard/components-next/onboarding/GuidedTour.vue';
import { useSidebarKeyboardShortcuts } from 'dashboard/components-next/sidebar/useSidebarKeyboardShortcuts';
import { isConversationRouteName } from 'dashboard/helper/conversationRoutes';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    WorkspaceV2Shell,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    FloatingCallWidget,
    MobileBottomNav,
    KanbanAppointmentModal,
    AgendaQuickPanel,
    FocusModeOverlay,
    PageCoach,
    PageManual,
    GuidedTour,
  },
  setup() {
    const { isWorkspaceV2 } = useWorkspaceV2();
    watch(isWorkspaceV2, () => resetOptimisticSettings());
    const { updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    // Armazenamento do navegador preso a conta: fixa o id para os helpers que
    // rodam fora de componente e faz, uma vez por carga, a migracao das chaves
    // antigas sem conta (rascunho de texto migra, o resto e descartado).
    watch(
      accountId,
      id => {
        definirContaAtual(id);
        garantirMigracaoDeArmazenamento(id);
      },
      { immediate: true }
    );
    // Boot dos dados da conta (inboxes, labels, times, pastas, funis...).
    // Morava no Sidebar antigo; o #483 apagou o componente e o boot junto.
    useAccountBootstrapData();
    const {
      applyFocusModeDefaultV2,
      applyListDefaultV3,
      applyDockCollisionV4,
    } = useFocusMode();
    const { isListHidden } = useWorkbenchLayout();
    const { width: windowWidth } = useWindowSize();
    const currentUser = useMapGetter('getCurrentUser');
    // SiteUp: o layout focado é o único. A migração v2 só aplica lista/painel
    // uma vez, a v3 devolve a lista de conversas a quem ela havia escondido,
    // e a v4 move o cluster só quando a posição salva tapa o hub.
    // focus_mode_enabled: false no ui_settings é ignorado.
    watch(
      () => currentUser.value?.id,
      id => {
        if (!id) return;
        applyFocusModeDefaultV2();
        applyListDefaultV3();
        applyDockCollisionV4();
      },
      { immediate: true }
    );
    // A v4 nao marca em viewport estreito. Se a pessoa abrir estreito e
    // depois alargar, tenta de novo. Depois de marcada, o apply e no-op.
    watch(windowWidth, () => {
      if (!currentUser.value?.id) return;
      applyDockCollisionV4();
    });
    const callsStore = useCallsStore();
    // SiteUp: estado do popup de agendamento (via evento global da API do Kanban).
    const {
      open: appointmentOpen,
      itemId: appointmentItemId,
      scheduledAt: appointmentScheduledAt,
    } = useKanbanAppointmentPopup();
    const { open: agendaQuickOpen } = useAgendaQuickPanel();

    const showShortcutModal = ref(false);
    useSidebarKeyboardShortcuts(show => {
      showShortcutModal.value = Boolean(show);
    });

    return {
      isWorkspaceV2,
      updateUISettings,
      accountId,
      windowWidth,
      isListHidden,
      showShortcutModal,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      appointmentOpen,
      appointmentItemId,
      appointmentScheduledAt,
      agendaQuickOpen,
    };
  },
  data() {
    return {
      showCreateAccountModal: false,
      hasRedirectedToHub: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    // SiteUp: dentro de uma conversa a bottom nav mobile some, então não
    // reservamos o padding inferior (evita espaço morto sob o campo de resposta).
    isConversationRoute() {
      return isConversationRouteName(this.$route.name);
    },
    themeScope() {
      return inheritsWorkspaceV2Theme(this.$route.path);
    },
  },
  watch: {
    // O tema segue a CHAVE de tema da tela, nao o prefixo /v2 do caminho: a
    // casa dos relatorios usa a chave da V2 sem estar sob /v2, e entrar nela
    // vindo de uma tela classica tambem precisa repintar.
    themeScope() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: this.isSmallScreen
            ? LAYOUT_TYPES.EXPANDED
            : LAYOUT_TYPES.CONDENSED,
        });
      },
      immediate: true,
    },
  },
  mounted() {
    document.body.classList.add('focus-lights-out');
    // O pouso pode acontecer antes do router resolver a rota inicial.
    this.maybeRedirectToHub();
    this.$router.isReady().then(() => this.maybeRedirectToHub());
  },
  unmounted() {
    document.body.classList.remove('focus-lights-out');
  },
  methods: {
    maybeRedirectToHub() {
      if (
        !this.isWorkspaceV2 &&
        !this.hasRedirectedToHub &&
        this.$route.name === 'home'
      ) {
        this.hasRedirectedToHub = true;
        this.$router.replace({
          name: 'focus_hub',
          params: { accountId: this.accountId },
        });
      }
    },
    openCreateAccountModal() {
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <component
    :is="isWorkspaceV2 ? 'WorkspaceV2Shell' : 'div'"
    class="flex flex-grow overflow-hidden text-n-slate-12"
    @show-create-account-modal="openCreateAccountModal"
    @open-key-shortcut-modal="toggleKeyShortcutModal"
  >
    <!-- SiteUp: dock pessoal de atalhos. Fica FORA do <main> e é
         position:fixed. Desktop apenas: no celular a barra inferior já
         cobre Hub, fila e destinos. -->
    <FocusModeOverlay
      v-if="!isWorkspaceV2 && !isListHidden && !isSmallScreen"
      @show-create-account-modal="openCreateAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
    />
    <!-- Dica por página: fora do v-if do overlay para pegar celular e lista oculta. -->
    <PageCoach />
    <PageManual />
    <GuidedTour />

    <!-- A pílula de vidro mede 62px e flutua a 10px do fundo: 72px no total.
         max-md:pb-20 (5rem) reserva isso com folga, senão o rodapé de
         paginação das listas fica atrás dela. Sem viewport-fit=cover não há
         safe-area a somar: env() vale 0 e o iOS pinta a faixa do gesto fora
         do viewport. Dentro de conversa a nav some e o padding sai junto. -->
    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
      :class="{ 'max-md:pb-20': !isWorkspaceV2 && !isConversationRoute }"
    >
      <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <router-view />
        </div>
      </div>
      <CommandBar />
      <MobileBottomNav v-if="!isWorkspaceV2" />
      <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
    <!-- SiteUp: popup de confirmação de agendamento WAHA — dispara pelo evento
         global 'kanban:appointment:scheduled' (ex.: agendar pelo painel Kanban da
         conversa). A tela do Kanban tem o seu próprio (rotas exclusivas). -->
    <KanbanAppointmentModal
      v-model="appointmentOpen"
      :item-id="appointmentItemId"
      :scheduled-at="appointmentScheduledAt"
    />
    <!-- SiteUp: Central de Agendamento (evento global 'agenda:quick:open'). -->
    <AgendaQuickPanel v-model="agendaQuickOpen" />
  </component>
</template>
